<template>
  <section id="customer-maintenance-history" ref="sheet" class="print-history" tabindex="-1">
    <div class="history-actions no-print">
      <button class="sheet-action" type="button" @click="print">Print or save PDF</button>
      <button v-if="closable" class="sheet-action close-action" type="button" @click="$emit('close')">Close history</button>
    </div>
    <header class="sheet-header">
      <p>Centerfire Services</p>
      <h2>Maintenance and repair history</h2>
      <dl>
        <div><dt>Machine</dt><dd>{{ machineName }}</dd></div>
        <div><dt>Centerfire machine number</dt><dd>{{ identity.tag ? `CFM# ${identity.tag}` : 'Not tagged' }}</dd></div>
        <div><dt>Current {{ meterUnit }}</dt><dd>{{ meterValue(currentHours) }}</dd></div>
      </dl>
    </header>
    <p v-if="!sheetRows.length" class="empty">No customer-visible maintenance entries have been recorded yet.</p>
    <table v-else class="history-table">
      <thead><tr><th>Date</th><th>{{ meterLabel }}</th><th>Type</th><th>Work performed</th><th>Notes</th></tr></thead>
      <tbody>
        <tr v-for="row in sheetRows" :key="row.id" :class="{ 'row-repair': row.category === 'Repair' }">
          <td>{{ row.displayDate || formatDate(row.at) }}</td>
          <td>{{ typeof row.hours === 'number' ? meterValue(row.hours) : '—' }}</td>
          <td>{{ row.category }}</td>
          <td>{{ row.title }}</td>
          <td>{{ row.detail }}</td>
        </tr>
      </tbody>
    </table>
    <p class="sheet-footer">
      {{ footerNote }}
      {{ machineName }}<span v-if="identity.tag"> · CFM# {{ identity.tag }}</span><span v-if="identity.serial"> · serial {{ identity.serial }}</span>
      <br />Printed {{ printedOn }}. Each maintenance and repair line comes from the customer-visible record for this machine.
    </p>
  </section>
</template>

<script>
/**
 * The single service-history document, shared by both tiers: the "View full history" section
 * inside the enrolled portal and the PIN-gated history a non-enrolled customer reaches from a
 * scanned tag render the identical sheet.
 *
 * It is machine-centric on purpose. Nothing identifying the owner is passed in or shown, so a
 * printed copy carries no customer name, address, or price — see shapeHistorySheet on the
 * service for the boundary that enforces the same rule server-side.
 *
 * "Print or save PDF" is the browser's own dialog. There is no generated PDF anywhere in this
 * system; this sheet plus a print stylesheet is the document.
 */
export default {
  name: 'HistorySheet',
  props: {
    identity: { type: Object, required: true },
    currentHours: { type: Number, default: null },
    meterUnit: { type: String, default: 'hours' },
    rows: { type: Array, default: () => [] },
    ownerGreasings: { type: Object, default: null },
    footerNote: { type: String, default: 'Maintenance and repair history recorded by Centerfire Services.' },
    closable: { type: Boolean, default: true },
  },
  emits: ['close'],
  computed: {
    machineName() {
      const named = [this.identity.make, this.identity.model].filter(Boolean).join(' ');
      return named || this.identity.publicLabel || this.identity.publicType || 'Machine';
    },
    meterLabel() { return this.meterUnit === 'miles' ? 'Miles' : 'Hours'; },
    printedOn() { return new Intl.DateTimeFormat('en-US', { dateStyle: 'long' }).format(new Date()); },
    sheetRows() {
      const rows = [...this.rows];
      // Greasings the owner logged since our last stop are a count, not work Centerfire performed,
      // so they lead the sheet as a single summary line rather than one row each.
      if (this.ownerGreasings?.count) {
        rows.unshift({
          id: 'owner-grease-activity',
          at: this.ownerGreasings.since,
          displayDate: this.ownerGreasings.since ? `Since ${this.formatDate(this.ownerGreasings.since)}` : 'Since the last service',
          hours: null,
          category: 'Owner grease',
          title: `${this.ownerGreasings.count} ${this.ownerGreasings.count === 1 ? 'greasing' : 'greasings'} logged by owner`,
          detail: 'Reported through this portal between scheduled Centerfire services.',
        });
      }
      return rows;
    },
  },
  methods: {
    focus() { this.$refs.sheet?.focus(); },
    print() { window.print(); },
    formatDate(value) {
      if (!value) return '—';
      const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
      const date = new Date(dateOnly ? `${value}T12:00:00Z` : value);
      return Number.isNaN(date.valueOf()) ? value : new Intl.DateTimeFormat('en-US', { dateStyle: 'long', ...(dateOnly ? { timeZone: 'UTC' } : {}) }).format(date);
    },
    meterValue(value) { return typeof value === 'number' ? `${value.toLocaleString()} ${this.meterUnit}` : 'Pending'; },
  },
};
</script>

<style scoped>
.print-history { scroll-margin-top: 130px; background: #fff; color: #111; outline: none; }
.history-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: .6rem; margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 1px solid #bbb; }
.sheet-action { padding: .65rem .9rem; border: 1px solid #111; background: #fff; color: #111; font: inherit; font-weight: 600; cursor: pointer; }
.sheet-action:hover,.sheet-action:focus-visible { background: #111; color: #fff; }
.close-action { border-color: var(--orange); color: #b33f00; }
.sheet-header > p { margin: 0; color: #b33f00; font-size: .78rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
.sheet-header h2 { margin: .3rem 0 1.2rem; font-size: clamp(1.8rem,4vw,3rem); font-weight: 500; }
.sheet-header dl { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); margin: 0 0 1.5rem; border: 1px solid #111; }
.sheet-header dl > div { padding: .75rem; border-right: 1px solid #111; }
.sheet-header dl > div:last-child { border-right: 0; }
.sheet-header dt { margin-bottom: .25rem; font-size: .68rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
.sheet-header dd { margin: 0; }
.empty { color: #444; }
.history-table { width: 100%; border-collapse: collapse; font-family: Arial,sans-serif; font-size: .78rem; line-height: 1.4; }
.history-table th,.history-table td { padding: .65rem; border: 1px solid #111; text-align: left; vertical-align: top; }
.history-table th { background: #eee; font-weight: 700; }
.history-table .row-repair td:first-child { border-left: 4px solid #b33f00; }
.sheet-footer { margin: 1.4rem 0 0; color: #444; font-family: Arial,sans-serif; font-size: .72rem; line-height: 1.5; }

@media (max-width: 760px) {
  .print-history { overflow-x: auto; }
  .history-table { min-width: 760px; }
  .sheet-header dl { grid-template-columns: 1fr; }
  .sheet-header dl > div { border-right: 0; border-bottom: 1px solid #111; }
  .sheet-header dl > div:last-child { border-bottom: 0; }
}

@media print {
  .print-history { display: block !important; overflow: visible; padding: 0; border: 0; }
  .no-print { display: none !important; }
  .history-table { min-width: 0; font-size: 9pt; }
  .history-table th,.history-table td { padding: 6pt; }
  .history-table tr { break-inside: avoid; }
}
</style>
